import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  Component,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  Optional,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors } from '@angular/forms';
import { PhxTimePickerFormat, PhxTimePickerTypes } from './phx-time-picker.type';
import mixinDisabled from '../../mixins/disabled';
import { Clock } from '../icon/icon-map';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import { phxWebStyleTokens } from '../../styles/phx-tokens';
import { PHX_LOCALE_PROVIDER, PhxLocaleProvider } from '../locale/phx-locale.token';

const baseInputMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: 'phx-time-input',
  templateUrl: './phx-time-picker-input.component.html',
  styleUrls: ['./phx-time-picker-input.component.scss'],
  inputs: ['phxDisabled'],
  host: {
    class: 'phx-time-picker-input',
    '[class.phx-time-picker-input-error]': 'phxError',
    '[class.phx-not-time-picker]': 'phxPanelTimePicker',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxTimePickerInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhxTimePickerInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxTimePickerInputComponent extends baseInputMixin implements ControlValueAccessor, Validator, OnChanges {
  @Input() public phxTimePickerFormat: PhxTimePickerFormat = 'default';
  @Input() public phxSuffix: TemplateRef<any>;
  @Input() public phxPrefix: TemplateRef<any>;
  @Input() public phxError: boolean = false;
  @Input() public phxPanelTimePicker: boolean = false;
  @Input() public phxDisableBefore: Date;
  @Input() public phxDisableAfter: Date;
  @Input() phxDisabledHours: number[] = [];
  @Input() phxDisabledMinutes: number[] = [];
  public readonly ClockIcon: string = Clock;
  public readonly ClockActiveColor = phxWebStyleTokens.colorContentActive;
  public hours: number | null = null;
  public minutes: number | null = null;
  public period: 'AM' | 'PM';
  public focusedField: PhxTimePickerTypes;
  public dropdownOpen: boolean = false;

  private isFocused: boolean = false;

  public get is24HourFormat(): boolean {
    return !!this.phxLocale?.uses24HourClock?.() || this.phxTimePickerFormat === '24H';
  }

  private onChange = (_: any) => {};
  private onTouched = () => {};

  constructor(
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private phxLocale: PhxLocaleProvider | null,
  ) {
    super();
  }

  get combinedValue(): Date {
    const date = new Date();
    const hours = this.hours !== null ? this.hours : 0;
    const minutes = this.minutes !== null ? this.minutes : 0;

    if (!this.is24HourFormat && this.period === 'PM' && hours < 12) {
      date.setHours(hours + 12);
    } else if (!this.is24HourFormat && this.period === 'AM' && hours === 12) {
      date.setHours(0);
    } else {
      date.setHours(hours);
    }
    date.setMinutes(minutes);
    date.setSeconds(0);
    date.setMilliseconds(0);

    return date;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxTimePickerFormat } = changes;
    if (phxTimePickerFormat && phxTimePickerFormat.currentValue) {
      this.toggleFormat();
    }
  }

  public setFocusedField(field: PhxTimePickerTypes) {
    if (this.phxDisabled) return;
    this.focusedField = field;
  }

  public togglePeriod() {
    if (this.phxDisabled) return;
    if (!this.is24HourFormat) {
      this.period = this.period === 'AM' ? 'PM' : 'AM';
      this.propagateChange();
    }
  }

  public toggleFormat() {
    if (this.is24HourFormat && this.period === 'PM') {
      this.hours = (this.hours % 12) + 12;
    } else if (!this.is24HourFormat && this.hours >= 12) {
      this.hours = this.hours % 12;
      this.period = 'PM';
    } else {
      this.period = 'AM';
    }
    this.propagateChange();
  }

  public onKeyDown(event: KeyboardEvent) {
    if (this.phxDisabled) return;
    if (event.key === 'Tab') {
      return;
    }
    event.preventDefault();
    if (event.key === KeyboardKeys.ARROW_UP || event.key === KeyboardKeys.ARROW_DOWN) {
      const increment = event.key === KeyboardKeys.ARROW_UP ? 1 : -1;
      if (this.focusedField === 'hour') {
        this.hours = this.adjustValue(this.hours, this.is24HourFormat ? 0 : 1, this.is24HourFormat ? 23 : 12, increment);
      } else if (this.focusedField === 'minute') {
        this.minutes = this.adjustValue(this.minutes, 0, 59, increment);
      } else if (this.focusedField === 'ampm') {
        this.togglePeriod();
        this.focusedField = 'hour';
      }
      this.propagateChange();
      return;
    } else if (event.key === KeyboardKeys.ARROW_RIGHT) {
      if (this.focusedField === 'minute' && !this.is24HourFormat) {
        this.focusedField = 'ampm';
      } else if (this.focusedField === 'hour') {
        this.focusedField = 'minute';
      } else {
        this.focusedField = 'hour';
      }
    } else if (event.key === KeyboardKeys.ARROW_LEFT) {
      if (this.focusedField === 'ampm') {
        this.focusedField = 'minute';
      } else if (this.focusedField === 'minute') {
        this.focusedField = 'hour';
      } else if (!this.is24HourFormat) {
        this.focusedField = 'ampm';
      } else {
        this.focusedField = 'hour';
      }
    }
    if (/^\d$/.test(event.key)) {
      if (this.focusedField === 'hour') {
        this.hours = this.updateValue(this.hours, this.is24HourFormat ? 0 : 1, this.is24HourFormat ? 23 : 12, event.key);
        if (this.hours >= 10) this.focusedField = 'minute';
      } else if (this.focusedField === 'minute') {
        this.minutes = this.updateValue(this.minutes, 0, 59, event.key);
        if (this.minutes >= 10) this.focusedField = 'ampm';
      }
    }
    this.propagateChange();
    setTimeout(() => {
      const element = event.target as HTMLElement;
      element.focus();
    }, 10);
  }

  public onFocus() {
    if (this.phxDisabled) return;
    this.isFocused = true;
    this.onTouched();
    if (!this.focusedField) this.focusedField = 'hour';
  }

  public onBlur() {
    this.isFocused = false;
    setTimeout(() => {
      if (!this.isFocused) {
        this.focusedField = null;
        this.cdr.detectChanges();
      }
    }, 10);
  }

  public onDropDownMenuVisibilityChange(event: boolean): void {
    this.dropdownOpen = event;
  }

  public dropDownValueChange(value: Date) {
    this.writeValue(value);
    this.propagateChange();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.phxDisabled = isDisabled;
  }

  public writeValue(value: Date): void {
    if (value) {
      if (this.phxDisableBefore && value < this.phxDisableBefore) {
        this.phxError = true;
        this.writeValue(null);
      }
      if (this.phxDisableAfter && value > this.phxDisableAfter) {
        this.phxError = true;
        this.writeValue(null);
      }
      value = new Date(value);
      this.hours = value.getHours();
      this.minutes = value.getMinutes();
      if (!this.is24HourFormat) {
        this.period = this.hours >= 12 ? 'PM' : 'AM';
        this.hours = this.hours % 12 || 12;
      }
      this.cdr.detectChanges();
    } else {
      this.hours = null;
      this.minutes = null;
      this.period = 'AM';
    }
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (this.phxDisableBefore && value < this.phxDisableBefore) {
      this.phxError = true;
      return { outOfRange: true };
    }
    if (this.phxDisableAfter && value > this.phxDisableAfter) {
      this.phxError = true;
      return { outOfRange: true };
    }
    this.phxError = false;
    return null;
  }

  private updateValue(current: number, min: number, max: number, key: string): number {
    let newValue = parseInt(key, 10);
    if (!isNaN(newValue)) {
      newValue = current * 10 + newValue;
      if (newValue > max) {
        newValue = newValue % 10;
      }
      if (newValue < min) {
        newValue = min;
      }
    }
    return newValue;
  }

  private propagateChange() {
    const value = this.combinedValue;
    this.onChange(value);
  }

  private adjustValue(current: number, min: number, max: number, increment: number): number {
    let newValue = current + increment;
    if (newValue > max) {
      newValue = min;
    } else if (newValue < min) {
      newValue = max;
    }
    return newValue;
  }
}
